import { useMemo, useRef } from 'react'
import type { JsonValue } from 'type-fest'
import {
	stringifySocketEntityLocation,
	type EntityModelType,
	type EntityOwner,
	type FeedbackEntityStyleOverride,
	type RawStoreResult,
	type SomeEntityModel,
	type SomeSocketEntityLocation,
} from '@companion-app/shared/Model/EntityModel.js'
import type { ExpressionOrValue } from '@companion-app/shared/Model/Options.js'
import type { VariableValue } from '@companion-app/shared/Model/Variables.js'
import type { GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { trpc, useMutationExt } from '~/Resources/TRPC'

export interface IEntityEditorService {
	readonly listId: SomeSocketEntityLocation
	readonly confirmModal: React.RefObject<GenericConfirmModalRef | null>

	addEntity: (
		connectionId: string,
		entityModelType: EntityModelType,
		definitionId: string,
		ownerId: EntityOwner | null
	) => Promise<string | null>

	setValue: (
		entityId: string,
		entity: SomeEntityModel | undefined,
		key: string,
		val: ExpressionOrValue<JsonValue | undefined>
	) => void
	performDelete: (entityId: string, entityTypeLabel: string) => void
	performDuplicate: (entityId: string) => void
	setConnection: (entityId: string, connectionId: string) => void
	moveCard: (
		dragListId: SomeSocketEntityLocation,
		dragEntityId: string,
		dropOwnerId: EntityOwner | null,
		dropIndex: number
	) => void
	performLearn: ((entityId: string) => void) | undefined
	setEnabled: ((entityId: string, enabled: boolean) => void) | undefined
	setHeadline: ((entityId: string, headline: string) => void) | undefined

	setRawStoreResult: ((entityId: string, target: RawStoreResult | undefined) => void) | undefined

	setInverted: (entityId: string, inverted: ExpressionOrValue<boolean>) => void
	setVariableName: (entityId: string, name: string) => void
	setVariableValue: (entityId: string, value: VariableValue) => void

	replaceStyleOverride: (entityId: string, override: FeedbackEntityStyleOverride) => void
	removeStyleOverride: (entityId: string, overrideId: string) => void
}

export interface IEntityEditorActionService {
	setValue: (key: string, val: ExpressionOrValue<JsonValue | undefined>) => void
	performDelete: () => void
	performDuplicate: () => void
	setConnection: (connectionId: string) => void
	performLearn: (() => void) | undefined
	setEnabled: ((enabled: boolean) => void) | undefined
	setHeadline: ((headline: string) => void) | undefined

	setRawStoreResult: ((target: RawStoreResult | undefined) => void) | undefined

	setInverted: (inverted: ExpressionOrValue<boolean>) => void
	setVariableName: (name: string) => void
	setVariableValue: (value: VariableValue) => void

	replaceStyleOverride: (override: FeedbackEntityStyleOverride) => void
	removeStyleOverride: (overrideId: string) => void
}

export function useControlEntitiesEditorService(
	controlId: string,
	listId: SomeSocketEntityLocation,
	confirmModal: React.RefObject<GenericConfirmModalRef | null>
): IEntityEditorService {
	const addMutation = useMutationExt(trpc.controls.entities.add.mutationOptions())
	const moveMutation = useMutationExt(trpc.controls.entities.move.mutationOptions())
	const setOptionMutation = useMutationExt(trpc.controls.entities.setOption.mutationOptions())
	const setConnectionMutation = useMutationExt(trpc.controls.entities.setConnection.mutationOptions())
	const removeMutation = useMutationExt(trpc.controls.entities.remove.mutationOptions())
	const duplicateMutation = useMutationExt(trpc.controls.entities.duplicate.mutationOptions())
	const learnOptionsMutation = useMutationExt(trpc.controls.entities.learnOptions.mutationOptions())
	const setEnabledMutation = useMutationExt(trpc.controls.entities.setEnabled.mutationOptions())
	const setHeadlineMutation = useMutationExt(trpc.controls.entities.setHeadline.mutationOptions())
	const setInvertedMutation = useMutationExt(trpc.controls.entities.setInverted.mutationOptions())
	const setRawStoreResultMutation = useMutationExt(trpc.controls.entities.setRawStoreResult.mutationOptions())
	const setVariableNameMutation = useMutationExt(trpc.controls.entities.setVariableName.mutationOptions())
	const setVariableValueMutation = useMutationExt(trpc.controls.entities.setVariableValue.mutationOptions())
	const replaceStyleOverrideMutation = useMutationExt(trpc.controls.entities.replaceStyleOverride.mutationOptions())
	const removeStyleOverrideMutation = useMutationExt(trpc.controls.entities.removeStyleOverride.mutationOptions())

	return useMemo(
		() => ({
			listId,
			confirmModal,

			addEntity: async (
				connectionId: string,
				entityModelType: EntityModelType,
				definitionId: string,
				ownerId: EntityOwner | null
			) => {
				return addMutation.mutateAsync({
					controlId,
					entityLocation: listId,
					ownerId,
					connectionId,
					entityType: entityModelType,
					entityDefinition: definitionId,
				})
			},

			moveCard: (
				moveEntityLocation: SomeSocketEntityLocation,
				moveEntityId: string,
				newOwnerId: EntityOwner | null,
				newIndex: number
			) => {
				moveMutation
					.mutateAsync({
						controlId,
						moveEntityLocation,
						moveEntityId,
						newOwnerId,
						newEntityLocation: listId,
						newIndex,
					})
					.catch((e) => {
						console.error('Failed to reorder control entities', e)
					})
			},

			setValue: (
				entityId: string,
				entity: SomeEntityModel | undefined,
				key: string,
				value: ExpressionOrValue<JsonValue | undefined>
			) => {
				if (!entity?.options || entity.options[key] !== value) {
					setOptionMutation
						.mutateAsync({
							controlId,
							entityLocation: listId,
							entityId,
							key,
							value,
						})
						.catch((e) => {
							console.error('Failed to set control entity option', e)
						})
				}
			},

			setConnection: (entityId: string, connectionId: string) => {
				setConnectionMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						connectionId,
					})
					.catch((e) => {
						console.error('Failed to set control entity connection', e)
					})
			},

			performDelete: (entityId: string, entityTypeLabel: string) => {
				confirmModal.current?.show(`Delete ${entityTypeLabel}`, `Delete ${entityTypeLabel}?`, 'Delete', () => {
					removeMutation
						.mutateAsync({
							controlId,
							entityLocation: listId,
							entityId,
						})
						.catch((e) => {
							console.error('Failed to remove control entity', e)
						})
				})
			},

			performDuplicate: (entityId: string) => {
				duplicateMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
					})
					.catch((e) => {
						console.error('Failed to duplicate control entity', e)
					})
			},

			performLearn: (entityId: string) => {
				learnOptionsMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
					})
					.catch((e) => {
						console.error('Failed to learn control entity values', e)
					})
			},

			setEnabled: (entityId: string, enabled: boolean) => {
				setEnabledMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						enabled,
					})
					.catch((e) => {
						console.error('Failed to enable/disable entity', e)
					})
			},
			setHeadline: (entityId: string, headline: string) => {
				setHeadlineMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						headline,
					})
					.catch((e) => {
						console.error('Failed to set entity headline', e)
					})
			},

			setInverted: (entityId: string, isInverted: ExpressionOrValue<boolean>) => {
				setInvertedMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						isInverted,
					})
					.catch((e) => {
						console.error('Failed to set entity inverted', e)
					})
			},

			setRawStoreResult: (entityId: string, target: RawStoreResult | undefined) => {
				setRawStoreResultMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						target,
					})
					.catch((e) => {
						console.error('Failed to set store action result target', e)
					})
			},

			setVariableName: (entityId: string, name: string) => {
				setVariableNameMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						name,
					})
					.catch((e) => {
						console.error('Failed to set entity variable name', e)
					})
			},
			setVariableValue: (entityId: string, value: JsonValue | undefined) => {
				setVariableValueMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						value,
					})
					.catch((e) => {
						console.error('Failed to set entity variable value', e)
					})
			},

			replaceStyleOverride: (entityId: string, override: FeedbackEntityStyleOverride) => {
				replaceStyleOverrideMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						override,
					})
					.catch((e) => {
						console.error('Failed to replace style override', e)
					})
			},
			removeStyleOverride: (entityId: string, overrideId: string) => {
				removeStyleOverrideMutation
					.mutateAsync({
						controlId,
						entityLocation: listId,
						entityId,
						overrideId,
					})
					.catch((e) => {
						console.error('Failed to remove style override', e)
					})
			},
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			addMutation,
			moveMutation,
			setOptionMutation,
			setConnectionMutation,
			removeMutation,
			duplicateMutation,
			learnOptionsMutation,
			setEnabledMutation,
			setHeadlineMutation,
			setInvertedMutation,
			setVariableNameMutation,
			setVariableValueMutation,
			replaceStyleOverrideMutation,
			removeStyleOverrideMutation,

			confirmModal,
			controlId,
			// eslint-disable-next-line react-hooks/use-memo
			stringifySocketEntityLocation(listId),
		]
	)
}

export function useControlEntityService(
	serviceFactory: IEntityEditorService,
	entity: SomeEntityModel,
	entityTypeLabel: string
): IEntityEditorActionService {
	const entityRef = useRef<SomeEntityModel>(entity)
	entityRef.current = entity

	const entityId = entity.id

	return useMemo(
		() => ({
			setValue: (key: string, val: ExpressionOrValue<JsonValue | undefined>) =>
				serviceFactory.setValue(entityId, entityRef.current, key, val),
			performDelete: () => serviceFactory.performDelete(entityId, entityTypeLabel),
			performDuplicate: () => serviceFactory.performDuplicate(entityId),
			setConnection: (connectionId: string) => serviceFactory.setConnection(entityId, connectionId),
			performLearn: serviceFactory.performLearn ? () => serviceFactory.performLearn?.(entityId) : undefined,
			setEnabled: serviceFactory.setEnabled
				? (enabled: boolean) => serviceFactory.setEnabled?.(entityId, enabled)
				: undefined,
			setHeadline: serviceFactory.setHeadline
				? (headline: string) => serviceFactory.setHeadline?.(entityId, headline)
				: undefined,
			setRawStoreResult: serviceFactory.setRawStoreResult
				? (target: RawStoreResult | undefined) => serviceFactory.setRawStoreResult?.(entityId, target)
				: undefined,
			setInverted: (inverted: ExpressionOrValue<boolean>) => serviceFactory.setInverted(entityId, inverted),
			setVariableName: (name: string) => serviceFactory.setVariableName(entityId, name),
			setVariableValue: (value: VariableValue) => serviceFactory.setVariableValue(entityId, value),
			replaceStyleOverride: (override: FeedbackEntityStyleOverride) =>
				serviceFactory.replaceStyleOverride(entityId, override),
			removeStyleOverride: (overrideId: string) => serviceFactory.removeStyleOverride(entityId, overrideId),
		}),
		[serviceFactory, entityId, entityTypeLabel]
	)
}
